import React from "react";
import { Skeleton } from "@mui/material";
import { TableRow, TableCell } from "@mui/material";

/**
 * TableBodySkeleton – renders MUI <TableRow> elements for use inside <TableBody>.
 * This keeps the layout stable (FilterBar + TableHead stay in place).
 *
 * Props:
 *  cols        – number of columns (default 5)
 *  rows        – number of shimmer rows (default 10)
 *  hasActions  – add a narrow actions cell at the end (default false)
 */
export const TableBodySkeleton = ({ cols = 5, rows = 10, hasActions = false }) => {
  return (
    <>
      {[...Array(rows)].map((_, ri) => (
        <TableRow
          key={ri}
          sx={{ background: ri % 2 === 0 ? "#fff" : "#f8fafc" }}
        >
          {/* Serial No */}
          <TableCell sx={{ width: 50 }}>
            <Skeleton variant="text" width={24} height={18} />
          </TableCell>

          {/* Data cells */}
          {[...Array(cols - (hasActions ? 2 : 1))].map((_, ci) => (
            <TableCell key={ci}>
              <Skeleton variant="text" height={18} />
            </TableCell>
          ))}

          {/* Actions cell */}
          {hasActions && (
            <TableCell align="center" sx={{ width: 80 }}>
              <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                <Skeleton variant="rounded" width={26} height={26} />
                <Skeleton variant="rounded" width={26} height={26} />
              </div>
            </TableCell>
          )}
        </TableRow>
      ))}
    </>
  );
};

/**
 * TableSkeleton – standalone full block skeleton (kept for backward compat / non-table pages).
 * Do NOT place before a FilterBar — use TableBodySkeleton inside <TableBody> instead.
 */
const TableSkeleton = ({ cols = 5, rows = 10, hasActions = false }) => {
  return (
    <div style={{ width: "100%", overflow: "hidden" }}>
      {/* Header row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `50px repeat(${cols - 1 + (hasActions ? 1 : 0)}, 1fr)`,
          gap: 8,
          padding: "10px 20px",
          background: "#f1f5f9",
          borderBottom: "2px solid #cbd5e1",
        }}
      >
        {[...Array(cols + (hasActions ? 1 : 0))].map((_, i) => (
          <Skeleton key={i} variant="text" height={14} />
        ))}
      </div>

      {/* Body rows */}
      {[...Array(rows)].map((_, ri) => (
        <div
          key={ri}
          style={{
            display: "grid",
            gridTemplateColumns: `50px repeat(${cols - 1 + (hasActions ? 1 : 0)}, 1fr)`,
            gap: 8,
            padding: "11px 20px",
            borderBottom: "1px solid #e2e8f0",
            background: ri % 2 === 0 ? "#ffffff" : "#f8fafc",
            alignItems: "center",
          }}
        >
          <Skeleton variant="text" width={24} height={16} />
          {[...Array(cols - (hasActions ? 2 : 1))].map((_, ci) => (
            <Skeleton key={ci} variant="text" height={16} />
          ))}
          {hasActions && (
            <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
              <Skeleton variant="rounded" width={28} height={28} />
              <Skeleton variant="rounded" width={28} height={28} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default TableSkeleton;
